import React, { useMemo, useState } from 'react';

import { Link as RouterLink } from '~/lib/react-router-dom';

import {
  Alert,
  Autocomplete,
  Box,
  CircularProgress,
  Link,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';
import { DateRange, DateRangePicker, PickersShortcutsItem } from '@mui/x-date-pickers-pro';

import { getCountryObjs } from '@luxuryescapes/lib-countries';

import { useGetContentTagPerformance } from '~/hooks/apis/experiences';
import useDebounced from '~/hooks/useDebounced';

import { ContentTagPerformanceParams, ContentTagPerformanceSort } from '~/services/ExperiencesService';
import { Dayjs, getDayJs } from '~/services/TimeService';

import { formatCurrency, formatNumber } from '../utils';

type CountryOption = { label: string; iso2: string };
type SortDirection = 'asc' | 'desc';

const PAGE_SIZE = 50;
const COUNTRY_OPTIONS: Array<CountryOption> = getCountryObjs()
  .map((country) => ({ label: country.countryName, iso2: country.iso2 }))
  .sort((left, right) => left.label.localeCompare(right.label));

const DATE_RANGE_SHORTCUTS: Array<PickersShortcutsItem<DateRange<Dayjs>>> = [
  {
    label: 'Last 7 Days',
    getValue: () => {
      const today = getDayJs();
      return [today.subtract(7, 'day'), today.subtract(1, 'day')];
    },
  },
  {
    label: 'Last 30 Days',
    getValue: () => {
      const today = getDayJs();
      return [today.subtract(30, 'day'), today.subtract(1, 'day')];
    },
  },
  {
    label: 'Last 90 Days',
    getValue: () => {
      const today = getDayJs();
      return [today.subtract(90, 'day'), today.subtract(1, 'day')];
    },
  },
];

function formatMarginPct(value: number): string {
  return new Intl.NumberFormat('en-AU', {
    style: 'percent',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(value);
}

function getDefaultDateRange(): DateRange<Dayjs> {
  const today = getDayJs();
  return [today.subtract(30, 'day'), today.subtract(1, 'day')];
}

export default function ContentTagPerformanceTab() {
  const [dateRange, setDateRange] = useState<DateRange<Dayjs>>(getDefaultDateRange);
  const [selectedCountry, setSelectedCountry] = useState<CountryOption | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [sort, setSort] = useState<ContentTagPerformanceSort>('bookings');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [page, setPage] = useState(0);
  const search = useDebounced(searchInput.trim(), 300);

  const params = useMemo<ContentTagPerformanceParams>(() => {
    const [from, to] = dateRange;
    return {
      from: from?.isValid() && to?.isValid() ? from.format('YYYY-MM-DD') : undefined,
      to: from?.isValid() && to?.isValid() ? to.format('YYYY-MM-DD') : undefined,
      countryCode: selectedCountry?.iso2,
      search: search || undefined,
      sort,
      sortDirection,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    };
  }, [dateRange, page, search, selectedCountry?.iso2, sort, sortDirection]);

  const { data, isLoading, isFetching, error } = useGetContentTagPerformance(params);
  const rows = data?.result ?? [];
  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const resetPage = () => setPage(0);

  const handleSort = (nextSort: ContentTagPerformanceSort) => {
    if (nextSort === sort) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
    } else {
      setSort(nextSort);
      setSortDirection(nextSort === 'name' ? 'asc' : 'desc');
    }
    resetPage();
  };

  const updatedAt = data?.updatedAt
    ? new Date(data.updatedAt).toLocaleString('en-AU', { dateStyle: 'short', timeStyle: 'short' })
    : 'N/A';

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        {error instanceof Error ? error.message : 'Failed to fetch Content Tag performance'}
      </Alert>
    );
  }

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h5" fontWeight="bold">
          Content Tag Performance
        </Typography>
        <Typography variant="body2" color="text.secondary" mt={0.5}>
          Experience purchases are attributed after a customer opens an experience from a Content Tag result list. Data
          refreshes daily.
        </Typography>
      </Box>

      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
        <DateRangePicker
          value={dateRange}
          onChange={(value) => {
            setDateRange(value);
            resetPage();
          }}
          maxDate={getDayJs().subtract(1, 'day')}
          localeText={{ start: 'Start date', end: 'End date' }}
          slotProps={{ shortcuts: { items: DATE_RANGE_SHORTCUTS }, textField: { size: 'small' } }}
        />
        <Autocomplete
          options={COUNTRY_OPTIONS}
          value={selectedCountry}
          onChange={(_, value) => {
            setSelectedCountry(value);
            resetPage();
          }}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, value) => option.iso2 === value.iso2}
          renderInput={(inputParams) => <TextField {...inputParams} label="Country" size="small" />}
          sx={{ minWidth: 220 }}
          clearOnEscape
        />
        <TextField
          label="Search tags"
          placeholder="Search by name..."
          value={searchInput}
          onChange={(event) => {
            setSearchInput(event.target.value);
            resetPage();
          }}
          size="small"
          sx={{ minWidth: 260 }}
        />
        {isFetching && <CircularProgress size={20} />}
      </Stack>

      <Typography variant="body2" color="text.secondary">
        Conversion attribution only. Showing {total === 0 ? 0 : page * PAGE_SIZE + 1}–
        {Math.min((page + 1) * PAGE_SIZE, total)} of {total} tags · Updated: {updatedAt}
      </Typography>

      {rows.length === 0 ? (
        <Alert severity="info">No attributed Experience bookings matched the selected filters.</Alert>
      ) : (
        <>
          <Table size="small">
            <TableHead>
              <TableRow>
                <SortableHeader
                  field="name"
                  label="Content Tag"
                  sort={sort}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                />
                <SortableHeader
                  field="bookings"
                  label="Bookings"
                  sort={sort}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  align="right"
                />
                <SortableHeader
                  field="revenue"
                  label="Revenue (AUD)"
                  sort={sort}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  align="right"
                />
                <SortableHeader
                  field="margin"
                  label="Margin (AUD)"
                  sort={sort}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  align="right"
                />
                <SortableHeader
                  field="marginPct"
                  label="Margin %"
                  sort={sort}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  align="right"
                />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.contentTagId} hover>
                  <TableCell>
                    <Link
                      component={RouterLink}
                      to={`/marketing/tags?tagId=${encodeURIComponent(row.contentTagId)}`}
                      fontWeight="medium"
                      underline="hover"
                    >
                      {row.name}
                    </Link>
                  </TableCell>
                  <TableCell align="right">{formatNumber(row.bookings)}</TableCell>
                  <TableCell align="right">{formatCurrency(row.revenue)}</TableCell>
                  <TableCell align="right">{formatCurrency(row.margin)}</TableCell>
                  <TableCell align="right">{formatMarginPct(row.marginPct)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {pageCount > 1 && (
            <Box display="flex" justifyContent="flex-end">
              <Pagination
                count={pageCount}
                page={page + 1}
                onChange={(_, nextPage) => setPage(nextPage - 1)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}
    </Stack>
  );
}

function SortableHeader({
  field,
  label,
  sort,
  sortDirection,
  onSort,
  align = 'left',
}: {
  field: ContentTagPerformanceSort;
  label: string;
  sort: ContentTagPerformanceSort;
  sortDirection: SortDirection;
  onSort: (field: ContentTagPerformanceSort) => void;
  align?: 'left' | 'right';
}) {
  return (
    <TableCell align={align}>
      <TableSortLabel
        active={sort === field}
        direction={sort === field ? sortDirection : 'desc'}
        onClick={() => onSort(field)}
      >
        <Typography fontWeight="bold" variant="body2">
          {label}
        </Typography>
      </TableSortLabel>
    </TableCell>
  );
}
